import { type FC, useContext } from 'react';
import Highcharts, { type PatternOptionsObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import PatternFillModule from 'highcharts/modules/pattern-fill';
import { groupBy } from 'lodash';
import { CheckCircle } from 'lucide-react';

import { useMe } from '@/hooks/use-me';
import { formatNumber } from '@/utils/format/number';
import {
  PenaltyDescriptionId,
  PenaltyDescriptionLabel,
} from '@/warranty/domain/entities/penalty-description-id';
import {
  type PenaltyOverviewChartData,
  type WarrantyPenalty,
} from '@/warranty/domain/entities/warranty-penalty-chart-data';
import { IWarrantyRepository } from '@/warranty/domain/repositories/warranty.repository';

import { WarrantyDetailsContext } from '../../pages/warranty-details';
import { GetWarrantyPenaltyChartData } from '../../queries/get-warranty-penalty-chart-data';
import { baseColors } from '../color-palette';
import { PlotSection } from '../plot-section';
import { ChartSkeleton } from '../skeletons/ChartSkeleton';

import { IntroBox } from './intro-box';
import { PenaltyOverviewTable } from './penalty-overview-table';

// make pattern-fill features available to all Highcharts instances
PatternFillModule(Highcharts);

const wideStripe: PatternOptionsObject = {
  path: {
    d: 'M 0 0 L 10 10',
    stroke: baseColors[4],
  },
  width: 8,
  height: 8,
};

export function aggregatePenaltiesForChart(
  data: PenaltyOverviewChartData[],
): WarrantyPenalty[] {
  const allPenalties = data.flatMap((d) => d.penalties);
  const byType = groupBy(allPenalties, (p) => p.type);

  // Ensure every known type is represented
  return Object.values(PenaltyDescriptionId).map((type) => {
    const penalties = byType[type];
    return {
      type,
      yearToDate: penalties
        ? Math.max(...penalties.map((p) => p.yearToDate))
        : 0,
      futurePrediction: penalties
        ? Math.max(...penalties.map((p) => p.futurePrediction))
        : 0,
      windowEndDate: penalties
        ?.map((p) => p.windowEndDate)
        .filter(Boolean)
        .reduce(
          (latest, current) =>
            !latest || (current && current > latest) ? current : latest,
          undefined as Date | undefined,
        ),
    };
  });
}

export const ZeroPenaltyState: FC = () => {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-xl border border-green-100 bg-green-50 p-6 text-center text-green-800 shadow-inner">
      <CheckCircle className="mb-3 size-10 text-green-600" />
      <div className="text-lg font-semibold">
        No warranty penalties detected
      </div>
      <div className="text-sm text-gray-600">
        All systems are operating within warranty conditions.
      </div>
    </div>
  );
};

export type PenaltyChartProps = {
  filters: IWarrantyRepository.Filters;
  // Add props if needed later
};

export const PenaltyChart: FC<PenaltyChartProps> = ({ filters }) => {
  const asset = useContext(WarrantyDetailsContext);
  const me = useMe();

  const warrantyPenalties = GetWarrantyPenaltyChartData.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
    assetId: asset!.id,
    customerId: me.config.id,
  });

  if (warrantyPenalties.isLoading) return <ChartSkeleton />;
  if (warrantyPenalties.isError)
    return <div>Error loading Warranty overview</div>;

  if (!warrantyPenalties.data) {
    return (
      <div className="py-4 text-center">
        <p>No data available</p>
      </div>
    );
  }

  // TODO: replace [0] with an aggregation logic before:
  const aggregatedPenalties = aggregatePenaltiesForChart(
    warrantyPenalties.data,
  );

  // detect all zero state for better visualization
  const allPenaltiesAreZero = aggregatedPenalties.every(
    (p) => p.yearToDate === 0 && p.futurePrediction === 0,
  );

  if (allPenaltiesAreZero) {
    return <ZeroPenaltyState />;
  }

  // Remove desciption IDs that are 0 only keep list of global penalties
  const filteredAggregated = aggregatedPenalties.filter(
    (p) => p.yearToDate !== 0 || p.futurePrediction !== 0,
  );

  // Sort by human-readable label (optional for stable order)
  const sortedAggregated = filteredAggregated.sort((a, b) => {
    const labelA = PenaltyDescriptionLabel[a.type] ?? a.type;
    const labelB = PenaltyDescriptionLabel[b.type] ?? b.type;
    return labelA.localeCompare(labelB);
  });

  // Final chart series and categories
  const categories = sortedAggregated.map(
    (p) => PenaltyDescriptionLabel[p.type] ?? p.type,
  );
  const endOfYearData = sortedAggregated.map((p) => p.futurePrediction);
  const yearToDateData = sortedAggregated.map((p) => p.yearToDate);

  const hasPredictionData = endOfYearData.some(
    (value, index) => value > 0 && value !== yearToDateData[index],
  );

  // Generate dynamic prediction label based on window end dates
  const generatePredictionLabel = (): string => {
    const windowDates = sortedAggregated
      .map((p) => p.windowEndDate)
      .filter(Boolean) as Date[];

    if (windowDates.length === 0) return 'Prediction';

    const today = new Date();
    const maxDate = new Date(Math.max(...windowDates.map((d) => d.getTime())));
    const diffInDays = Math.ceil(
      (maxDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffInDays <= 30) return 'Prediction (1 month)';
    if (diffInDays <= 90) return 'Prediction (3 months)';
    if (diffInDays <= 180) return 'Prediction (6 months)';
    if (diffInDays <= 365) return 'Prediction (1 year)';
    return 'Prediction (long-term)';
  };

  const series: Highcharts.SeriesOptionsType[] = [];

  // Add future predictions first (bottom of stack)
  if (hasPredictionData) {
    series.push({
      name: generatePredictionLabel(),
      type: 'bar',
      data: endOfYearData,
      color: { pattern: wideStripe },
      stack: 'penalties',
      zIndex: 0,
    });
  }

  // Add historical data second (top of stack)
  series.push({
    name: 'Current (YTD)',
    type: 'bar',
    data: yearToDateData,
    color: baseColors[4],
    stack: 'penalties',
    zIndex: 1,
  });

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
    },
    title: {
      text:
        me.config.id === '69' ? 'SOH Warranty Penalties' : 'Warranty Penalties',
    },
    xAxis: {
      categories,
      title: {
        text: null,
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Penalty Points',
      },
      labels: {
        overflow: 'justify',
      },
    },
    plotOptions: {
      bar: {
        stacking: 'normal',
        shadow: false,
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.1,
      },
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const points = this.points || [this.point];
        let historical = 0;
        let future = 0;
        let predictionLabel = 'Prediction';

        points.forEach((point) => {
          if (point.series.name === 'Current (YTD)') {
            historical = Number(point.y);
          } else if (point.series.name?.startsWith('Prediction')) {
            future = Number(point.y);
            predictionLabel = point.series.name;
          }
        });

        const total = historical + future;
        let tooltip = `<b>${this.x}</b><br/>`;

        if (historical > 0) {
          tooltip += `Current (YTD): <strong>${formatNumber(historical, 2)}</strong><br/>`;
        }
        if (future > 0) {
          tooltip += `${predictionLabel}: <strong>${formatNumber(future, 2)}</strong><br/>`;
          // add deadline information
          const idx = categories.indexOf(this.x as string);
          const deadline = sortedAggregated[idx]?.windowEndDate;
          if (deadline) {
            const today = new Date();
            const diff = Math.ceil(
              (deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
            );
            tooltip += `Applies on <strong>${deadline.toLocaleDateString()}</strong>`;
            if (diff >= 0) {
              tooltip += ` (in ${diff} days)`;
            }
            tooltip += '<br/>';
          }
        }
        if (total > 0) {
          tooltip += `<b>Total Expected: ${formatNumber(total, 2)}</b>`;
        }

        return tooltip;
      },
    },
    series,
    legend: {
      enabled: true,
    },
    credits: {
      enabled: false,
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export type PenaltyOverviewProps = {
  filters: IWarrantyRepository.Filters;
};
export function PenaltyOverview({ filters }: PenaltyOverviewProps) {
  return (
    <PlotSection sectionName="Penalty Overview">
      <IntroBox />
      <div className="flex flex-row gap-x-10">
        <div className="h-96 w-2/5 flex-1">
          <PenaltyChart filters={filters} />
        </div>

        <div className="h-96 w-2/5 flex-1">
          <PenaltyOverviewTable filters={filters} />
        </div>
      </div>
    </PlotSection>
  );
}
